import React, { useEffect, useState } from "react";

import {
  FileErrors,
  FileValidators,
  useFileHandler,
} from "hooks/useFileHandler";
import { useAppDispatch, useAppSelector } from "utils/store";
import { strings } from "intl/fa";
import CardWrapper from "components/commons/cardWrapper/CardWrapper";
import styles from "../Invoicing.module.scss";
import classNames from "classnames";
import Footer from "components/commons/footer/Footer";
import { Button, ButtonSize } from "@bale/ui";
import { GeneralErrors } from "hooks/useInputHandler";
import { useRequest } from "hooks/useRequest";
import { getPanelsThunk, setAuthDataThunk } from "core/auth/auth.thunk";
import {
  inputLengths,
  OrganizationAuthStepsArr,
} from "../OrganizationInvoicing";
import { uploadFile } from "core/file/file.api";
import { AuthActions } from "core/auth/auth.slice";
import {
  NavigationActions,
  AppPage,
  OrganizationAuthSteps,
} from "core/navigation/navigation.slice";
import { SuccessfulAuthModal } from "components/modals/successfulAuthModal/SuccessfulAuthModal";
import FileInput from "components/commons/fileInput/FileInput";
import { ProgressSteps } from "components/commons/progressSteps/ProgressSteps";
import { rejectionReasonsErrors } from "utils/auth";

export function DocumentsStep() {
  const dispatch = useAppDispatch();
  const { sendRequest: setAuthData } = useRequest(setAuthDataThunk);
  const authDraft = useAppSelector((state) => state.auth.authenticationDraft);
  const { sendRequest: getPanel } = useRequest(getPanelsThunk);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const myPanel = useAppSelector((state) => state.auth.myPanel);

  const establishmentFileHandler = useFileHandler({
    rules: [
      FileValidators.required,
      FileValidators.maximumSize(inputLengths.fileSize),
      FileValidators.mediaType(["image/jpeg", "image/png", "application/pdf"]),
    ],
    errorMessages: {
      [GeneralErrors.Required]: strings.common.error.required,
      [FileErrors.MaximumSize]: strings.common.error.file_max_size,
      [FileErrors.MediaType]: strings.common.error.file_type,
    },
  });
  const changesFileHandler = useFileHandler({
    rules: [
      FileValidators.required,
      FileValidators.maximumSize(inputLengths.fileSize),
      FileValidators.mediaType(["image/jpeg", "image/png", "application/pdf"]),
    ],
    errorMessages: {
      [GeneralErrors.Required]: strings.common.error.required,
      [FileErrors.MaximumSize]: strings.common.error.file_max_size,
      [FileErrors.MediaType]: strings.common.error.file_type,
    },
  });
  const permissionFileHandler = useFileHandler({
    rules: [
      FileValidators.required,
      FileValidators.maximumSize(inputLengths.fileSize),
      FileValidators.mediaType(["image/jpeg", "image/png", "application/pdf"]),
    ],
    errorMessages: {
      [GeneralErrors.Required]: strings.common.error.required,
      [FileErrors.MaximumSize]: strings.common.error.file_max_size,
      [FileErrors.MediaType]: strings.common.error.file_type,
    },
  });

  const inputsAreValid = Boolean(
    establishmentFileHandler.file &&
      changesFileHandler.file &&
      permissionFileHandler.file
  );

  useEffect(() => {
    const rejectionErrorIds = myPanel.reason.split(",");

    if (rejectionErrorIds.includes("102")) {
      establishmentFileHandler.setError(rejectionReasonsErrors["102"]);
    }

    if (rejectionErrorIds.includes("103")) {
      changesFileHandler.setError(rejectionReasonsErrors["103"]);
    }

    if (rejectionErrorIds.includes("104")) {
      permissionFileHandler.setError(rejectionReasonsErrors["104"]);
    }
  }, [myPanel.reason]);

  useEffect(() => {
    if (establishmentFileHandler.file) {
      uploadFile({ file: establishmentFileHandler.file }).then((response) => {
        dispatch(
          AuthActions.setAuthenticationDraft({
            establishment_file_id: response.response?.file_id,
          })
        );
      });
    }
  }, [establishmentFileHandler.file]);

  useEffect(() => {
    if (changesFileHandler.file) {
      uploadFile({ file: changesFileHandler.file }).then((response) => {
        dispatch(
          AuthActions.setAuthenticationDraft({
            changes_file_id: response.response?.file_id,
          })
        );
      });
    }
  }, [changesFileHandler.file]);

  useEffect(() => {
    if (permissionFileHandler.file) {
      uploadFile({ file: permissionFileHandler.file }).then((response) => {
        dispatch(
          AuthActions.setAuthenticationDraft({
            permission_file_id: response.response?.file_id,
          })
        );
      });
    }
  }, [permissionFileHandler.file]);

  const submitClickHandler = () => {
    if (
      !authDraft.establishment_file_id ||
      !authDraft.changes_file_id ||
      !authDraft.permission_file_id
    ) {
      return;
    }
    setAuthData({
      ...myPanel,
      full_name: authDraft.full_name,
      address: authDraft.address,
      land_line_phone: authDraft.phone,
      zip_code: authDraft.zip_code,

      shenase_melli: authDraft.shenase_melli,
      economic_number: authDraft.economic_number,

      establishment_file_id: authDraft.establishment_file_id,
      changes_file_id: authDraft.changes_file_id,
      permission_file_id: authDraft.permission_file_id,
    }).then(() => {
      setShowSuccessModal(true);
    });
  };

  const successModalSubmitHandler = () => {
    getPanel().then(() => {
      dispatch(NavigationActions.setAppPage(AppPage.OrdersList));
      dispatch(
        NavigationActions.setOrganizationAuthSteps(
          OrganizationAuthSteps.BasicInformation
        )
      );
    });
  };

  return (
    <>
      <CardWrapper className={styles.Card}>
        <div className={styles.Form}>
          <div className={classNames(styles.HelpsWrapper, styles.FormItem)}>
            <div className={styles.HelpMain}>{strings.invoicing.help_main}</div>
            <div className={styles.HelpDesc}>
              {strings.invoicing.help_organization}
            </div>
          </div>
        </div>
        <ProgressSteps
          currentStep={OrganizationAuthSteps.Documents}
          steps={OrganizationAuthStepsArr}
          showOnmobile
        />
        <div>
          <div className={styles.ImageInputTitle}>
            {strings.invoicing.establishment_notice}
          </div>
          <FileInput
            {...establishmentFileHandler}
            label={strings.common.input.file.label}
          />
        </div>
        <div>
          <div className={styles.ImageInputTitle}>
            {strings.invoicing.notice_of_changes}
          </div>
          <FileInput
            {...changesFileHandler}
            label={strings.common.input.file.label}
          />
        </div>
        <div>
          <div className={styles.ImageInputTitle}>
            {strings.invoicing.activity_notice}
          </div>
          <FileInput
            {...permissionFileHandler}
            label={strings.common.input.file.label}
          />
        </div>
      </CardWrapper>
      <Footer>
        <Button
          onClick={submitClickHandler}
          text={strings.common.button.submit_and_continue}
          size={ButtonSize.XLarge}
          disableThemeOnly={!inputsAreValid}
        />
      </Footer>
      {showSuccessModal && (
        <SuccessfulAuthModal
          onClose={successModalSubmitHandler}
          onSubmit={successModalSubmitHandler}
        />
      )}
    </>
  );
}
